import { createContext, useContext } from "react";

export type DockDragSource = "floating" | "docked";

/** Per-slot positioning data registered by `DockManager` for hit-testing. */
export interface DockSlotMeasurement {
    id: string;
    /** Pixel offset from the dock zone's top edge. */
    top: number;
    height: number;
}

export interface DockDragState {
    isDragging: boolean;
    source: DockDragSource | null;
    draggingId: string | null;
    isInZone: boolean;
    /** Real-slot insertion index 0..N where the panel would land on release. */
    insertionIndex: number | null;
}

export interface DockLivePosition {
    x: number;
    y: number;
}

/** Transient `dockedHeight` override for one slot during an active resize drag. */
export interface DockLiveResize {
    id: string;
    height: number;
}

/** Transient dock column width override during an active width-resize drag. */
export interface DockLiveWidth {
    width: number;
}

export interface DockDragController {
    /** Render-time snapshot. From inside event handlers use `getState()` instead. */
    state: DockDragState;
    /** Latest state from a ref; safe to call from `pointerup`. */
    getState: () => DockDragState;
    registerZone: (rect: DOMRect | null) => void;
    registerSlots: (slots: DockSlotMeasurement[]) => void;
    beginDrag: (source: DockDragSource, id: string) => void;
    updatePointer: (clientX: number, clientY: number) => void;
    endDrag: () => void;
    /** Live cursor position for a docked drag-out; bypasses the reducer to avoid
     *  re-rendering `useWindows` consumers on every pointermove. `endDrag` clears. */
    setLivePosition: (pos: DockLivePosition | null) => void;
    getLivePosition: () => DockLivePosition | null;
    /** `useSyncExternalStore`-friendly: identity-stable across renders. */
    subscribeLivePosition: (listener: () => void) => () => void;
    /**
     * Live `dockedHeight` override for the slot mid-resize. Same rationale as
     * `setLivePosition`. `useDockLayout` substitutes this for the slot's stored
     * `dockedHeight` while the gesture is active.
     */
    setLiveResize: (resize: DockLiveResize | null) => void;
    getLiveResize: () => DockLiveResize | null;
    subscribeLiveResize: (listener: () => void) => () => void;
    /** Committed dock column width. Initialized to `DOCK_PANEL_WIDTH`. */
    dockWidth: number;
    /** Persist the final width after a drag gesture completes. */
    setDockWidth: (width: number) => void;
    /** Live dock column width override during a width-resize drag. Same
     *  ref+listener pattern as `setLiveResize` — avoids 60Hz re-renders. */
    setLiveWidth: (width: DockLiveWidth | null) => void;
    getLiveWidth: () => DockLiveWidth | null;
    subscribeLiveWidth: (listener: () => void) => () => void;
}

export const IDLE_DOCK_DRAG_STATE: DockDragState = {
    isDragging: false,
    source: null,
    draggingId: null,
    isInZone: false,
    insertionIndex: null,
};

export const DockDragContext = createContext<DockDragController | null>(null);

export function useDockDragController(): DockDragController {
    const ctx = useContext(DockDragContext);
    if (!ctx) {
        throw new Error("useDockDragController must be used within a DockDragProvider");
    }
    return ctx;
}

/** Insertion index 0..N for a pointer Y (zone-local). N means "after the last slot". */
export function computeInsertionIndex(pointerYInZone: number, slots: DockSlotMeasurement[]): number {
    if (slots.length === 0) {
        return 0;
    }
    for (let i = 0; i < slots.length; i++) {
        const slot = slots[i];
        const midpoint = slot.top + slot.height / 2;
        if (pointerYInZone < midpoint) {
            return i;
        }
    }
    return slots.length;
}
